/**
 * What you can do with a study, phrased as goals rather than operations.
 *
 * The tool catalog answers "what functions exist" — 92 of them, named after
 * the library call underneath (`Permute Axes`, `Multi-Otsu`). That is the
 * right list for someone who already knows what they want. It is the wrong
 * front door: a reader opening a stroke study is not looking for an operation,
 * they are looking for an answer.
 *
 * So each entry here names an outcome, says what it needs, and points at the
 * thing that actually delivers it — a pipeline preset, one catalog tool, or a
 * screen. Nothing is invented: `useCaseIssues()` in ./validate.ts fails the
 * build if an entry points at a tool id that does not exist, and a use case
 * whose tool is `future` inherits that state rather than promising anything.
 *
 * `audience` drives the Clinical / Research switch. Clinical is deliberately
 * short — a stroke read is four or five things, not fifty — and everything
 * else is research. `both` shows in either.
 */

import type { DwiStage, PipelineKind, PwiStage } from "@/ipc/contract";

export type Audience = "clinical" | "research" | "both";

export type UseCaseAction =
  /** A preset run of the OpenADS pipeline. */
  | { kind: "pipeline"; pipelineKind: PipelineKind; dwi?: DwiStage[]; pwi?: PwiStage[] }
  /** One tool from the catalog, opened with its parameters in the Study panel. */
  | { kind: "tool"; toolId: string }
  /** A whole screen, for things that are not a single operation. */
  | {
      kind: "screen";
      tab: "home" | "view" | "pipeline" | "results" | "developer" | "dataset" | "learning";
    }
  /** The batch runner over a dataset root. */
  | { kind: "batch"; pipelineKind: PipelineKind };

export interface UseCase {
  id: string;
  category: CategoryId;
  /**
   * The three OpenADS pipeline runs. This app is general-purpose imaging
   * software whose subject is acute stroke, and these are what it is for;
   * they lead the list and get their own row on the Start screen. Everything
   * else supports them.
   */
  featured?: boolean;
  title: string;
  /** What you get. Written as a result, not as a method. */
  outcome: string;
  audience: Audience;
  /** Preconditions, in the user's words. Empty means "an open study". */
  needs?: string[];
  action: UseCaseAction;
  /** Set only when the underlying capability is not wired up yet. */
  unavailable?: string;
  keywords?: string[];
}

export type CategoryId =
  | "read"
  | "stages"
  | "quantify"
  | "perfusion"
  | "segment"
  | "register"
  | "prepare"
  | "inspect"
  | "data"
  | "learning";

export const CATEGORIES: { id: CategoryId; label: string; blurb: string }[] = [
  { id: "read", label: "Stroke read", blurb: "Core lesion, ASPECTS and the radiological report." },
  {
    id: "learning",
    label: "ML / DL",
    blurb: "Training, evaluating and comparing the models the pipeline runs. Planned, not built.",
  },
  { id: "stages", label: "Pipeline stages", blurb: "Run one step of the OpenADS pipeline on its own." },
  { id: "quantify", label: "Quantification", blurb: "Volumes, regional involvement and atlas phenotypes." },
  { id: "perfusion", label: "Perfusion", blurb: "TTP, hypoperfusion and mismatch." },
  { id: "segment", label: "Segmentation", blurb: "Produce and edit lesion masks." },
  { id: "register", label: "Registration", blurb: "Put images in a comparable space." },
  { id: "prepare", label: "Image preparation", blurb: "Geometry, intensity and correction." },
  { id: "inspect", label: "Inspect & QC", blurb: "Read headers, compare volumes, check a run." },
  { id: "data", label: "Data in and out", blurb: "Import studies, export results." },
];

const DWI_ALL: DwiStage[] = [
  "prepdata",
  "gen_mask",
  "skull_strip",
  "registration",
  "inference",
  "report",
];
const PWI_ALL: PwiStage[] = [
  "prepdata",
  "gen_mask",
  "skull_strip",
  "gen_ttp",
  "registration",
  "ttpadc_coreg",
  "inference",
  "report",
];

export const USE_CASES: UseCase[] = [
  // ------------------------------------------- the three pipeline runs ---
  // Deliberately first, and deliberately only three. Everything below is in
  // service of these.
  {
    id: "read.dwi",
    category: "read",
    featured: true,
    title: "Acute stroke analysis (DWI)",
    outcome:
      "The full DWI pipeline: stroke core volume, ASPECTS, affected territories and the radiological report.",
    audience: "both",
    needs: ["DWI", "ADC or a b0 to compute it from"],
    action: { kind: "pipeline", pipelineKind: "dwi", dwi: DWI_ALL },
    keywords: ["acute", "stroke", "core", "infarct", "dagmnet", "dwi", "pipeline"],
  },
  {
    id: "read.pwi",
    category: "read",
    featured: true,
    title: "Perfusion analysis (PWI)",
    outcome:
      "The full PWI pipeline: TTP maps, hypoperfused tissue and its volume, with the perfusion report.",
    audience: "both",
    needs: ["4D PWI with its JSON sidecar"],
    action: { kind: "pipeline", pipelineKind: "pwi", pwi: PWI_ALL },
    keywords: ["perfusion", "pwi", "ttp", "hypoperfusion", "dsc", "pipeline"],
  },
  {
    id: "read.mismatch",
    category: "read",
    featured: true,
    title: "Core and perfusion mismatch (DWI + PWI)",
    outcome:
      "Both pipelines together: core against hypoperfusion, with the mismatch volume and ratio.",
    audience: "both",
    needs: ["DWI", "4D PWI with its JSON sidecar"],
    action: { kind: "pipeline", pipelineKind: "combined", dwi: DWI_ALL, pwi: PWI_ALL },
    keywords: ["mismatch", "penumbra", "ttp", "perfusion", "thrombectomy", "combined"],
  },
  {
    id: "read.segmentOnly",
    category: "read",
    title: "Segment the core on an already-prepared study",
    outcome: "A stroke core mask, without redoing preprocessing and registration.",
    audience: "research",
    needs: ["A study that already has registration output"],
    action: { kind: "pipeline", pipelineKind: "dwi", dwi: ["inference"] },
    keywords: ["inference", "rerun", "dagmnet"],
  },
  {
    id: "read.reportOnly",
    category: "read",
    title: "Regenerate the report",
    outcome: "The radiological report and overlay figures, from masks that already exist.",
    audience: "both",
    needs: ["A completed segmentation"],
    action: { kind: "pipeline", pipelineKind: "dwi", dwi: ["report"] },
    keywords: ["report", "pdf", "figures", "regenerate"],
  },
  {
    id: "read.aspects",
    category: "read",
    title: "Score ASPECTS",
    outcome: "The predicted MCA-ASPECTS, with the per-region involvement behind it.",
    audience: "both",
    needs: ["A stroke mask in MNI space"],
    action: { kind: "tool", toolId: "q.aspects" },
    keywords: ["aspects", "score", "mca"],
  },
  {
    id: "read.pcaspects",
    category: "read",
    title: "Score posterior circulation (pc-ASPECTS)",
    outcome: "Posterior-circulation regional involvement from the pc-ASPECTS atlas.",
    audience: "both",
    needs: ["A stroke mask in MNI space"],
    action: { kind: "tool", toolId: "q.regional" },
    keywords: ["pc-aspects", "posterior", "basilar", "cerebellum"],
  },
  {
    id: "read.review",
    category: "read",
    title: "Review a finished case",
    outcome: "Every number, table and figure a completed run produced, in one place.",
    audience: "both",
    action: { kind: "screen", tab: "results" },
    keywords: ["results", "summary", "review"],
  },

  // ----------------------------------------------------------- stages ---
  // The same pipeline, one step at a time, for re-running a stage that failed
  // or was tuned. Each is the identical OpenADS call the full run makes.
  {
    id: "stage.prepdata",
    category: "stages",
    title: "Prepare raw data",
    outcome: "DWI, ADC and b0 read, checked and written into the case layout. ADC is computed if absent.",
    audience: "research",
    needs: ["A subject folder"],
    action: { kind: "tool", toolId: "prep.prepdata" },
    keywords: ["prepdata", "stage 1", "raw", "import", "adc"],
  },
  {
    id: "stage.genMask",
    category: "stages",
    title: "Generate the brain mask",
    outcome: "A SynthStrip brain mask. On CPU this is the slowest stage of the run.",
    audience: "research",
    needs: ["Prepared DWI"],
    action: { kind: "tool", toolId: "prep.brainMask" },
    keywords: ["gen_mask", "stage 2", "synthstrip", "brain"],
  },
  {
    id: "stage.skullStrip",
    category: "stages",
    title: "Apply the brain mask",
    outcome: "Skull-stripped DWI, ADC and b0.",
    audience: "research",
    needs: ["A brain mask"],
    action: { kind: "tool", toolId: "prep.skullStrip" },
    keywords: ["skull_strip", "stage 3", "strip", "brain"],
  },
  {
    id: "stage.registration",
    category: "stages",
    title: "Register to MNI",
    outcome: "Affine and SyN transforms to MNI152 with the normalised volumes.",
    audience: "research",
    needs: ["Skull-stripped DWI and ADC"],
    action: { kind: "tool", toolId: "reg.dwiMni" },
    keywords: ["registration", "stage 4", "mni", "ants", "syn"],
  },
  {
    id: "stage.inference",
    category: "stages",
    title: "Run the segmentation model",
    outcome: "DAGMNet stroke core prediction, in MNI and back in the patient's own space.",
    audience: "research",
    needs: ["Registered, normalised DWI and ADC"],
    action: { kind: "tool", toolId: "seg.stroke" },
    keywords: ["inference", "stage 5", "dagmnet", "model", "predict"],
  },
  {
    id: "stage.report",
    category: "stages",
    title: "Quantify and report",
    outcome: "Atlas QFV tables, lesion-load tables, the radiological report and the overlay figures.",
    audience: "research",
    needs: ["A stroke mask"],
    action: { kind: "tool", toolId: "q.report" },
    keywords: ["report", "stage 6", "qfv", "cal_qfv", "figures"],
  },
  {
    id: "stage.adcMni",
    category: "stages",
    title: "Register ADC to MNI",
    outcome: "The ADC map carried into MNI space through the DWI transform.",
    audience: "research",
    needs: ["A DWI-to-MNI transform"],
    action: { kind: "tool", toolId: "reg.adcMni" },
    keywords: ["adc", "mni", "register", "preset"],
  },
  {
    id: "stage.pwiHp",
    category: "stages",
    title: "Run the hypoperfusion model",
    outcome: "The PWI DAGMNet prediction of hypoperfused tissue.",
    audience: "research",
    needs: ["A coregistered TTP map"],
    action: { kind: "tool", toolId: "seg.hp" },
    keywords: ["hypoperfusion", "pwi", "inference", "model"],
  },
  {
    id: "stage.ttpDwi",
    category: "stages",
    title: "Register TTP to DWI",
    outcome: "The TTP map on the diffusion grid, so core and perfusion can be compared.",
    audience: "research",
    needs: ["A TTP map", "DWI"],
    action: { kind: "tool", toolId: "reg.ttpDwi" },
    keywords: ["ttp", "dwi", "coregister", "preset"],
  },
  {
    id: "stage.pwiReport",
    category: "stages",
    title: "Generate the perfusion report",
    outcome: "The PWI report with hypoperfusion volume and mismatch.",
    audience: "research",
    needs: ["A hypoperfusion mask"],
    action: { kind: "tool", toolId: "q.pwiReport" },
    keywords: ["pwi", "report", "perfusion", "mismatch"],
  },
  {
    id: "stage.maskVolume",
    category: "stages",
    title: "Measure any mask's volume",
    outcome: "Voxel count and millilitres for whichever mask is selected.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.volume" },
    keywords: ["volume", "mask", "voxels", "ml"],
  },

  // --------------------------------------------------------- quantify ---
  {
    id: "quant.ownMask",
    category: "quantify",
    title: "Quantify a lesion mask you already have",
    outcome: "Atlas quantification for a mask you drew or another model produced — no DAGMNet run.",
    audience: "research",
    needs: ["A lesion mask", "A DWI to register it through"],
    action: { kind: "tool", toolId: "q.qfv" },
    keywords: ["manual", "external", "bring your own", "qfv", "byo"],
  },
  {
    id: "quant.coreVolume",
    category: "quantify",
    title: "Measure lesion volume",
    outcome: "Volume on the patient's own grid and in MNI space, with the voxel counts behind both.",
    audience: "both",
    needs: ["A lesion mask"],
    action: { kind: "tool", toolId: "q.coreVolume" },
    keywords: ["volume", "ml", "size", "core"],
  },
  {
    id: "quant.hpVolume",
    category: "quantify",
    title: "Measure hypoperfusion volume",
    outcome: "Hypoperfused tissue volume from the TTP-derived mask.",
    audience: "both",
    needs: ["A hypoperfusion mask"],
    action: { kind: "tool", toolId: "q.hpVolume" },
    keywords: ["hypoperfusion", "volume", "ttp"],
  },
  {
    id: "quant.lobe",
    category: "quantify",
    title: "Lobar involvement",
    outcome: "How much of each lobe the lesion occupies, per side.",
    audience: "research",
    needs: ["A stroke mask in MNI space"],
    action: { kind: "tool", toolId: "q.regional" },
    keywords: ["lobe", "frontal", "parietal", "temporal", "occipital"],
  },
  {
    id: "quant.vascular",
    category: "quantify",
    title: "Vascular territory involvement",
    outcome: "Lesion load per arterial territory — ACA, MCA, PCA, lenticulostriate.",
    audience: "both",
    needs: ["A stroke mask in MNI space"],
    action: { kind: "tool", toolId: "q.regional" },
    keywords: ["vascular", "territory", "aca", "mca", "pca", "artery"],
  },
  {
    id: "quant.ventricles",
    category: "quantify",
    title: "Ventricular involvement",
    outcome: "Lesion overlap with the ventricular system.",
    audience: "research",
    needs: ["A stroke mask in MNI space"],
    action: { kind: "tool", toolId: "q.regional" },
    keywords: ["ventricle", "hydrocephalus", "csf"],
  },
  {
    id: "quant.qfvTable",
    category: "quantify",
    title: "Read the multi-atlas QFV table",
    outcome: "The quantitative feature vector across all six atlases, as the pipeline wrote it.",
    audience: "research",
    needs: ["A completed run"],
    action: { kind: "tool", toolId: "q.qfv" },
    keywords: ["qfv", "atlas", "feature vector", "table"],
  },
  {
    id: "quant.cohort",
    category: "quantify",
    title: "Export imaging phenotypes for a cohort",
    outcome:
      "Run a folder of subjects and collect every QFV and lesion-load table — one row per patient, ready for modelling.",
    audience: "research",
    needs: ["A dataset folder of subject directories"],
    action: { kind: "batch", pipelineKind: "dwi" },
    keywords: ["batch", "cohort", "phenotype", "csv", "dataset", "tabular"],
  },
  {
    id: "quant.compareRuns",
    category: "quantify",
    title: "Compare a prediction against ground truth",
    outcome: "Dice, precision and sensitivity where the study carries a reference lesion.",
    audience: "research",
    needs: ["A run whose subject included a ground-truth mask"],
    action: { kind: "screen", tab: "results" },
    keywords: ["dice", "ground truth", "accuracy", "validation"],
  },

  // -------------------------------------------------------- perfusion ---
  {
    id: "perf.validate",
    category: "perfusion",
    title: "Check a 4D perfusion series",
    outcome: "Whether the series has enough timepoints and the sidecar needed for slice timing.",
    audience: "both",
    needs: ["4D PWI"],
    action: { kind: "tool", toolId: "pwi.validate" },
    keywords: ["4d", "validate", "timepoints", "sidecar", "dsc"],
  },
  {
    id: "perf.prepare",
    category: "perfusion",
    title: "Prepare perfusion data",
    outcome: "Motion-corrected, slice-time-corrected PWI ready for map generation.",
    audience: "research",
    needs: ["4D PWI with its JSON sidecar"],
    action: { kind: "tool", toolId: "pwi.prepare" },
    keywords: ["motion", "slice timing", "preprocess", "moco"],
  },
  {
    id: "perf.ttp",
    category: "perfusion",
    title: "Generate a TTP map",
    outcome: "Time-to-peak computed from the concentration curves.",
    audience: "both",
    needs: ["Prepared PWI"],
    action: { kind: "tool", toolId: "pwi.ttp" },
    keywords: ["ttp", "time to peak", "map", "perfusion"],
  },
  {
    id: "perf.hp",
    category: "perfusion",
    title: "Segment hypoperfused tissue",
    outcome: "A hypoperfusion mask from the TTP map.",
    audience: "both",
    needs: ["A TTP map"],
    action: { kind: "tool", toolId: "pwi.hp" },
    keywords: ["hypoperfusion", "penumbra", "segment"],
  },
  {
    id: "perf.coreg",
    category: "perfusion",
    title: "Align perfusion to diffusion",
    outcome: "TTP resampled onto the DWI grid so core and hypoperfusion are comparable.",
    audience: "research",
    needs: ["A TTP map", "DWI"],
    action: { kind: "tool", toolId: "pwi.coreg" },
    keywords: ["coregister", "ttp", "dwi", "align"],
  },
  {
    id: "perf.mismatch",
    category: "perfusion",
    title: "Compute mismatch",
    outcome: "Mismatch volume and ratio between hypoperfusion and core.",
    audience: "both",
    needs: ["A core mask", "A hypoperfusion mask"],
    action: { kind: "tool", toolId: "pwi.mismatch" },
    keywords: ["mismatch", "ratio", "penumbra", "salvageable"],
  },
  {
    id: "perf.tmax",
    category: "perfusion",
    title: "Generate Tmax / CBF / CBV",
    outcome: "Deconvolution-based perfusion maps.",
    audience: "research",
    action: { kind: "tool", toolId: "pwi.tmax" },
    unavailable: "OpenADS computes TTP, not Tmax. Calling TTP a Tmax would be wrong.",
    keywords: ["tmax", "cbf", "cbv", "mtt", "deconvolution"],
  },

  // ---------------------------------------------------------- segment ---
  {
    id: "seg.core",
    category: "segment",
    title: "Segment the stroke core with the AI model",
    outcome: "A DAGMNet core mask in native and MNI space.",
    audience: "both",
    needs: ["Registered, skull-stripped DWI and ADC"],
    action: { kind: "tool", toolId: "seg.stroke" },
    keywords: ["dagmnet", "ai", "core", "infarct", "model"],
  },
  {
    id: "seg.brain",
    category: "segment",
    title: "Extract the brain",
    outcome: "A brain mask via SynthStrip.",
    audience: "both",
    needs: ["DWI"],
    action: { kind: "tool", toolId: "seg.brain" },
    keywords: ["synthstrip", "brain mask", "skull strip", "bet"],
  },
  {
    id: "seg.threshold",
    category: "segment",
    title: "Threshold by intensity",
    outcome: "A mask of everything inside an intensity range.",
    audience: "research",
    action: { kind: "tool", toolId: "seg.threshold" },
    keywords: ["threshold", "intensity", "binarize"],
  },
  {
    id: "seg.otsu",
    category: "segment",
    title: "Find a threshold automatically",
    outcome: "An Otsu-chosen split between foreground and background.",
    audience: "research",
    action: { kind: "tool", toolId: "seg.otsu" },
    keywords: ["otsu", "automatic", "threshold"],
  },
  {
    id: "seg.multiOtsu",
    category: "segment",
    title: "Split into several intensity classes",
    outcome: "A multi-level Otsu label map.",
    audience: "research",
    action: { kind: "tool", toolId: "seg.multiOtsu" },
    keywords: ["multi-otsu", "classes", "tissue"],
  },
  {
    id: "seg.largest",
    category: "segment",
    title: "Keep only the largest lesion",
    outcome: "The biggest connected component, by physical volume.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.components" },
    keywords: ["largest", "component", "connected", "clean"],
  },
  {
    id: "seg.countLesions",
    category: "segment",
    title: "Count separate lesions",
    outcome: "How many connected components the mask has, and how big each is.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.componentCount" },
    keywords: ["multifocal", "count", "components", "lesions"],
  },
  {
    id: "seg.removeSmall",
    category: "segment",
    title: "Remove speckle from a mask",
    outcome: "A mask with components under a size cut-off dropped.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.removeSmall" },
    keywords: ["despeckle", "noise", "small objects", "clean"],
  },
  {
    id: "seg.morphology",
    category: "segment",
    title: "Grow, shrink or close a mask",
    outcome: "Dilate, erode, open or close, by a radius you choose.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.dilate" },
    keywords: ["dilate", "erode", "morphology", "open", "close", "smooth"],
  },
  {
    id: "seg.fillHoles",
    category: "segment",
    title: "Fill holes in a mask",
    outcome: "Interior gaps closed without changing the outer boundary.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.fillHoles" },
    keywords: ["holes", "fill", "interior"],
  },
  {
    id: "seg.maskLogic",
    category: "segment",
    title: "Combine two masks",
    outcome: "Intersection, union, difference or inverse of a pair of masks.",
    audience: "research",
    needs: ["Two masks in the same space"],
    action: { kind: "tool", toolId: "seg.maskAnd" },
    keywords: ["and", "or", "xor", "not", "boolean", "combine", "overlap"],
  },
  {
    id: "seg.bbox",
    category: "segment",
    title: "Locate a lesion",
    outcome: "Bounding box and centre of mass, in voxels and millimetres.",
    audience: "research",
    needs: ["A mask"],
    action: { kind: "tool", toolId: "seg.bbox" },
    keywords: ["bounding box", "centroid", "location", "position"],
  },

  // --------------------------------------------------------- register ---
  {
    id: "reg.toMni",
    category: "register",
    title: "Register a study to MNI",
    outcome: "Affine and SyN transforms to MNI152, plus the resampled volumes.",
    audience: "both",
    needs: ["Skull-stripped DWI and ADC"],
    action: { kind: "tool", toolId: "reg.affinesyn" },
    keywords: ["mni", "register", "ants", "syn", "affine", "normalise"],
  },
  {
    id: "reg.quick",
    category: "register",
    title: "Register quickly (affine only)",
    outcome: "An affine-only alignment, for when SyN is more than you need.",
    audience: "research",
    action: { kind: "tool", toolId: "reg.quick" },
    keywords: ["quick", "affine", "fast", "rough"],
  },
  {
    id: "reg.qc",
    category: "register",
    title: "Check a registration",
    outcome: "The moving image over the fixed one, to judge the alignment by eye.",
    audience: "both",
    needs: ["A registered pair"],
    action: { kind: "tool", toolId: "reg.overlay" },
    keywords: ["qc", "overlay", "check", "alignment", "quality"],
  },
  {
    id: "reg.similarity",
    category: "register",
    title: "Score how well two images align",
    outcome: "Correlation and mean squared error between two volumes.",
    audience: "research",
    needs: ["Two volumes on the same grid"],
    action: { kind: "tool", toolId: "reg.similarityQc" },
    keywords: ["similarity", "correlation", "mse", "metric"],
  },
  {
    id: "reg.inspectTx",
    category: "register",
    title: "Inspect a transform",
    outcome: "What a stored .mat or warp field contains and which direction it goes.",
    audience: "research",
    needs: ["A transform file"],
    action: { kind: "tool", toolId: "reg.inspectTx" },
    keywords: ["transform", "matrix", "warp", "mat", "direction"],
  },
  {
    id: "reg.resampleRef",
    category: "register",
    title: "Put an image on another image's grid",
    outcome: "Resampled onto a reference — nearest neighbour for labels, linear for intensity.",
    audience: "research",
    needs: ["An image", "A reference image"],
    action: { kind: "tool", toolId: "prep.resampleRef" },
    keywords: ["resample", "reference", "grid", "match", "regrid"],
  },
  {
    id: "reg.apply",
    category: "register",
    title: "Apply an existing transform",
    outcome: "Move an image or mask through a transform you already have.",
    audience: "research",
    action: { kind: "tool", toolId: "reg.apply" },
    unavailable: "Apply-transform is not exposed as a standalone OpenADS command yet.",
    keywords: ["apply", "transform", "warp", "resample"],
  },

  // ---------------------------------------------------------- prepare ---
  {
    id: "prep.n4",
    category: "prepare",
    title: "Correct bias field",
    outcome: "N4-corrected intensities, so tissue of one type looks the same everywhere.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.n4" },
    keywords: ["n4", "bias", "inhomogeneity", "shading"],
  },
  {
    id: "prep.denoise",
    category: "prepare",
    title: "Reduce noise",
    outcome: "A denoised volume with edges preserved.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.denoise" },
    keywords: ["denoise", "noise", "smooth"],
  },
  {
    id: "prep.normalize",
    category: "prepare",
    title: "Normalise intensities",
    outcome: "Z-scored or rescaled values, so volumes can be compared or fed to a model.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.normalize" },
    keywords: ["normalize", "z-score", "standardise", "scale"],
  },
  {
    id: "prep.histogramMatch",
    category: "prepare",
    title: "Match one image's histogram to another",
    outcome: "Intensities remapped onto a reference distribution.",
    audience: "research",
    needs: ["An image", "A reference image"],
    action: { kind: "tool", toolId: "prep.histogramMatch" },
    keywords: ["histogram", "match", "harmonise", "intensity"],
  },
  {
    id: "prep.resample",
    category: "prepare",
    title: "Change voxel size",
    outcome: "Resampled to the spacing or matrix size you specify.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.resample" },
    keywords: ["resample", "spacing", "resolution", "isotropic", "voxel size"],
  },
  {
    id: "prep.cropPad",
    category: "prepare",
    title: "Crop or pad a volume",
    outcome: "A smaller field of view, or a larger canvas, without moving the anatomy.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.crop" },
    keywords: ["crop", "pad", "fov", "trim", "extend"],
  },
  {
    id: "prep.extract",
    category: "prepare",
    title: "Take one volume out of a 4D series",
    outcome: "A single 3D frame, by index.",
    audience: "research",
    needs: ["A 4D series"],
    action: { kind: "tool", toolId: "prep.extract" },
    keywords: ["4d", "frame", "extract", "volume", "timepoint"],
  },
  {
    id: "prep.reorient",
    category: "prepare",
    title: "Reorient to RAS+",
    outcome: "Axes reordered to the convention the pipeline expects.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.reorient" },
    keywords: ["ras", "orientation", "reorient", "axes", "las"],
  },
  {
    id: "prep.flip",
    category: "prepare",
    title: "Flip or permute axes",
    outcome: "Mirrored or transposed geometry, when a study was stored the wrong way round.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.flip" },
    keywords: ["flip", "mirror", "permute", "transpose", "left right"],
  },
  {
    id: "prep.applyMask",
    category: "prepare",
    title: "Apply a brain mask",
    outcome: "Everything outside the mask set to zero.",
    audience: "research",
    needs: ["An image", "A mask"],
    action: { kind: "tool", toolId: "prep.applyMask" },
    keywords: ["mask", "apply", "skull strip", "zero"],
  },
  {
    id: "prep.math",
    category: "prepare",
    title: "Add, subtract or multiply two images",
    outcome: "A voxelwise combination of a pair of volumes.",
    audience: "research",
    needs: ["Two volumes in the same physical space"],
    action: { kind: "tool", toolId: "prep.mathAdd" },
    keywords: ["add", "subtract", "multiply", "math", "arithmetic"],
  },
  {
    id: "prep.cast",
    category: "prepare",
    title: "Change the data type",
    outcome: "Recast to uint8, int16, float32 and so on.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.cast" },
    keywords: ["dtype", "cast", "int16", "float", "uint8"],
  },

  // ---------------------------------------------------------- inspect ---
  {
    id: "insp.header",
    category: "inspect",
    title: "Read the NIfTI header",
    outcome: "Dimensions, spacing, data type and orientation, without loading the voxels.",
    audience: "both",
    action: { kind: "tool", toolId: "prep.header" },
    keywords: ["header", "nifti", "metadata", "dimensions", "spacing"],
  },
  {
    id: "insp.affine",
    category: "inspect",
    title: "See the affine matrix",
    outcome: "The 4x4 that maps voxels to world coordinates.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.affineMatrix" },
    keywords: ["affine", "matrix", "sform", "world", "coordinates"],
  },
  {
    id: "insp.qform",
    category: "inspect",
    title: "Compare qform and sform",
    outcome: "Both transforms and their codes, when they disagree.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.qform" },
    keywords: ["qform", "sform", "code", "disagree"],
  },
  {
    id: "insp.orientation",
    category: "inspect",
    title: "Check image orientation",
    outcome: "The axis codes — RAS+, LAS+ and so on — as stored.",
    audience: "both",
    action: { kind: "tool", toolId: "prep.imageOrientation" },
    keywords: ["orientation", "ras", "las", "radiological", "neurological"],
  },
  {
    id: "insp.stats",
    category: "inspect",
    title: "Get intensity statistics",
    outcome: "Min, max, mean, percentiles and non-zero count, optionally inside a mask.",
    audience: "research",
    action: { kind: "tool", toolId: "prep.info" },
    keywords: ["statistics", "mean", "percentile", "histogram", "intensity"],
  },
  {
    id: "insp.difference",
    category: "inspect",
    title: "Difference two volumes",
    outcome: "A voxelwise difference image, to see what changed.",
    audience: "research",
    needs: ["Two volumes in the same physical space"],
    action: { kind: "tool", toolId: "reg.difference" },
    keywords: ["difference", "compare", "subtract", "changed"],
  },
  {
    id: "insp.jobLog",
    category: "inspect",
    title: "See why a run failed",
    outcome: "The pipeline log for a run, with the stage it stopped at.",
    audience: "both",
    action: { kind: "screen", tab: "pipeline" },
    keywords: ["log", "failed", "error", "debug", "why"],
  },
  {
    id: "insp.engine",
    category: "inspect",
    title: "Check the processing engine",
    outcome: "Which engine will run the pipeline, on what device, and what is missing.",
    audience: "both",
    action: { kind: "screen", tab: "developer" },
    keywords: ["engine", "docker", "python", "gpu", "cpu", "setup", "install"],
  },

  // ------------------------------------------------------------- data ---
  {
    id: "data.openSubject",
    category: "data",
    title: "Open a subject folder",
    outcome: "A study loaded from NIfTI files on disk.",
    audience: "both",
    action: { kind: "screen", tab: "home" },
    keywords: ["open", "load", "nifti", "subject", "folder"],
  },
  {
    id: "data.importDicom",
    category: "data",
    title: "Import from DICOM",
    outcome: "Series discovered, assigned to roles and converted to the layout the pipeline reads.",
    audience: "both",
    needs: ["A folder of DICOM files"],
    action: { kind: "screen", tab: "home" },
    keywords: ["dicom", "import", "pacs", "convert", "dcm"],
  },
  {
    id: "data.reviewDataset",
    category: "data",
    title: "Scan a folder of subjects",
    outcome:
      "Every subject read at once: what each has, how they are named, which files the pipeline will not find, whose images disagree with the rest, and which are ready to run.",
    audience: "both",
    needs: ["A folder with one subdirectory per subject"],
    action: { kind: "screen", tab: "dataset" },
    keywords: [
      "dataset", "cohort", "batch", "inventory", "audit", "review", "curation",
      "missing", "incomplete", "consistency", "header", "quality", "qc", "check",
      "scan", "naming", "screen", "screening",
    ],
  },
  {
    id: "data.openDataset",
    category: "data",
    title: "Run many subjects (batch)",
    outcome:
      "A folder of subject directories, filtered to the ones that can run, queued one after another.",
    audience: "research",
    // Was `screen`, which only changed tab: it landed on the run form in
    // whichever mode it was already in, so the entrance advertised here did not
    // visibly exist. `batch` switches the form to many-subject mode and asks for
    // the folder.
    action: { kind: "batch", pipelineKind: "dwi" },
    keywords: ["dataset", "cohort", "many", "batch", "folder", "multiple", "subjects", "queue", "bulk"],
  },
  {
    id: "data.export",
    category: "data",
    title: "Export results",
    outcome: "Tables, report, figures and masks written to a folder you choose.",
    audience: "both",
    needs: ["A completed run"],
    action: { kind: "screen", tab: "results" },
    keywords: ["export", "csv", "pdf", "nifti", "json", "save", "share"],
  },
  {
    id: "data.reopen",
    category: "data",
    title: "Reopen a previous case",
    outcome: "A case you ran before, with its outputs still attached.",
    audience: "both",
    action: { kind: "screen", tab: "home" },
    keywords: ["recent", "reopen", "history", "previous"],
  },

  /* ------------------------------------------------------------- ML / DL ---
   * The six placeholder training/evaluation use cases that used to live here
   * (export a training set, fine-tune, cross-validate, evaluate, compare,
   * manage weights — all `unavailable`, all pointing at a `learning` screen)
   * were removed 2026-10-01: the user defined real content for Tools > ML/DL
   * instead — see `ml.sam`/`ml.medsam` in `tools/catalog.ts`, reached as
   * `tool(...)` leaves, not `useCase(...)`, since picking a point on an image
   * and running a model does not fit this module's "outcome you get, no
   * engine required" shape. `learning/LearningTab.tsx` and the `"learning"`
   * tab it rendered for these are now unreachable from the menu bar; left in
   * place rather than excavated, same as the Visualization actions the prior
   * pass folded in here without deleting.
   */
];

export const USE_CASES_BY_CATEGORY = CATEGORIES.map((c) => ({
  ...c,
  items: USE_CASES.filter((u) => u.category === c.id),
}));

/** Free-text match over title, outcome and keywords. */
export function searchUseCases(query: string, audience: Audience | "all"): UseCase[] {
  const q = query.trim().toLowerCase();
  const inAudience = (u: UseCase) =>
    audience === "all" || u.audience === "both" || u.audience === audience;
  if (!q) return USE_CASES.filter(inAudience);
  return USE_CASES.filter(
    (u) =>
      inAudience(u) &&
      (u.title.toLowerCase().includes(q) ||
        u.outcome.toLowerCase().includes(q) ||
        (u.keywords ?? []).some((k) => k.includes(q))),
  );
}
